import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import LeadForm from "@/components/feature/LeadForm";
import FaqAccordion from "@/components/feature/FaqAccordion";
import CtaBanner from "@/components/feature/CtaBanner";
import { contactInfo } from "@/mocks/site";
import { HERO_IMAGES } from "@/config/heroImages";

const contactFaqs = [
  {
    q: "How do I get a moving quote?",
    a: "Call or WhatsApp us with a rough list of items and your locations, or fill the form on this page. We share a clear, item-wise estimate, usually the same day.",
  },
  {
    q: "How far in advance should I book my move?",
    a: "For local Bangalore shifting we can often accommodate you within 2 to 3 days. For month-end dates, intercity moves or larger households, booking 5 to 7 days ahead secures your preferred slot.",
  },
  {
    q: "Do you bring packing material and labour?",
    a: "Yes. Our crew arrives with in-house cartons, bubble wrap, foam, stretch film and wooden crates, along with trained staff for packing, loading, unloading and unpacking.",
  },
  {
    q: "Is my goods safe and insured during transit?",
    a: "Every move is handled by uniformed professionals and moved in covered-body vehicles. Transit insurance can be arranged on request before the move begins.",
  },
  {
    q: "Can you store my goods if my new home is not ready?",
    a: "Absolutely. Our own secured warehouse in Bangalore offers short and long term storage, so your goods can be held safely until you are ready to receive them.",
  },
  {
    q: "Which areas in Bangalore do you service?",
    a: "We cover all of Bangalore including Nagarabhavi, Rajajinagar, Malleswaram, Jayanagar, Whitefield, HSR, Banashankari and Mysore Road, plus intercity moves to Mysore and Tumkur.",
  },
];

const CONTACT_FORM_ID = "contact-form";

interface ContactCard {
  icon: string;
  title: string;
  lines: string[];
  href?: string;
  action?: string;
}

const contactCards: ContactCard[] = [
  {
    icon: "ri-phone-line",
    title: "Call Us",
    lines: [contactInfo.phone, contactInfo.phoneAlt],
    href: contactInfo.phoneHref,
    action: "Tap to call",
  },
  {
    icon: "ri-mail-line",
    title: "Email Us",
    lines: [contactInfo.email],
    href: contactInfo.emailHref,
    action: "Tap to email",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Visit Us",
    lines: [contactInfo.address],
    href: contactInfo.mapLink,
    action: "Open in maps",
  },
  {
    icon: "ri-time-line",
    title: "Working Hours",
    lines: [contactInfo.hoursWeek, contactInfo.hoursSun, contactInfo.support],
  },
];

export default function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact Us"
        title="Talk to Lahari Packers and Movers"
        subtitle="Ready to plan your move? Call us, WhatsApp us or drop your details below and a coordinator will get back to you shortly."
        crumbs={[{ label: "Home", href: "/" }, { label: "Contact us" }]}
        image={HERO_IMAGES.contact}
      />

      {/* Form + map */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-14">
            <div>
              <SectionHeading
                align="left"
                eyebrow="Send a Message"
                title="Tell us how we can help"
                subtitle="Share your shifting requirement and we will reply with a clear, item-wise estimate. No spam, ever."
              />
              <div className="mt-6">
                <LeadForm
                  formId={CONTACT_FORM_ID}
                  title="Contact Lahari Packers and Movers"
                  subtitle="Fill in your details and our team will call you back."
                  submitLabel="SEND MESSAGE"
                  serviceOptions={[
                    "House Shifting",
                    "Office Shifting",
                    "Storage Service",
                    "Warehouse Facility",
                    "Packing Only",
                    "Other Enquiry",
                  ]}
                />
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="relative min-h-[340px] flex-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 md:min-h-[460px]">
                <iframe
                  title="Lahari Packers and Movers Bangalore location map"
                  src={contactInfo.mapEmbed}
                  className="absolute inset-0 h-full w-full"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
                <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50/95 px-3 py-2.5 backdrop-blur-sm md:inset-x-4 md:bottom-4">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground-950 md:text-sm">
                    <span className="flex h-2.5 w-2.5 items-center justify-center">
                      <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                    </span>
                    Service available 24x7
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={contactInfo.phoneHref}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-phone-fill" aria-hidden="true" /> Call
                    </a>
                    <a
                      href={contactInfo.whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#1EBE5B]"
                    >
                      <i className="ri-whatsapp-line" aria-hidden="true" /> WhatsApp
                    </a>
                    <a
                      href={contactInfo.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      <i className="ri-direction-line" aria-hidden="true" /> Directions
                    </a>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">Reach us directly</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  Need an answer right now? Our helpline is open 24 x 7 for quotes, packing advice and shifting support.
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={contactInfo.phoneHref}
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-phone-fill text-lg" aria-hidden="true" /> Call Now
                  </a>
                  <a
                    href={contactInfo.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1EBE5B]"
                  >
                    <i className="ri-whatsapp-line text-lg" aria-hidden="true" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {contactCards.map((card) => (
              <div key={card.title} className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${card.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{card.title}</h3>
                <div className="mt-2 flex-1 space-y-1">
                  {card.lines.map((line) => (
                    <p key={line} className="text-sm leading-relaxed text-foreground-600">
                      {card.href ? (
                        <a
                          href={card.href}
                          target={card.href.startsWith("http") ? "_blank" : undefined}
                          rel={card.href.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="transition-colors hover:text-primary-600"
                        >
                          {line}
                        </a>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
                {card.action && (
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600">
                    {card.action} <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
            <div>
              <SectionHeading
                align="left"
                eyebrow="Good to Know"
                title="Moving questions, answered"
                subtitle="A few things customers ask us before booking a move. Still unsure? Call us any time."
              />
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950">Still have a question?</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  Our helpline is open 24 x 7 for quotes, packing advice and shifting support.
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={contactInfo.phoneHref}
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-phone-fill text-lg" aria-hidden="true" /> Call Now
                  </a>
                  <a
                    href={contactInfo.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1EBE5B]"
                  >
                    <i className="ri-whatsapp-line text-lg" aria-hidden="true" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
            <FaqAccordion items={contactFaqs} />
          </div>
        </div>
      </section>

      <CtaBanner
        title="Prefer a written quote instead?"
        description="Share your requirement on the quote page and we will send an item-wise estimate for your move."
      />
    </>
  );
}